import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, exhaustMap, catchError, filter, switchMap, take, tap, mergeMap } from 'rxjs/operators';
import { selectFiltersRequest, selectPayload } from './dcu-to-mtu-communication.reducer';
import { Observable, of, withLatestFrom } from 'rxjs';
import { DcuToMtuCommunicationActions } from './dcu-to-mtu-communication.actions';
import { DcuToMtuCommunicationService } from '../dcu-to-mtu-communication.service';
import { DevicesPayload } from '../../../equipment/devices/components/devices.models';
import { TableData } from '../../../../shared/types/api-response.types';
import { DcuToMtuCommunication, UserConfigDcuToMtuFilters } from '../dcu-to-mtu-communication.types';
import { ExportRow } from '../../../../shared/types/export.types';
import { DcuToMtuCommunicationModelService } from '../dcu-to-mtu-communication-model.service';
import { UserConfigService } from 'src/app/services/utility/user-config.service';
import { AsyncRequestStatus } from '@portal/shared/store/request-status';
import { ExportUtilsService } from 'src/app/services/utility/export-utils.service';
import { ExportFacadeService } from 'src/app/services/utility/export-facade.service';
import { ListingPageModel } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model.types';

const gridBaseKey = 'mtu-dcu-communication';

export const fetchData$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(DcuToMtuCommunicationActions.fetchData),
      mergeMap(() => [
        DcuToMtuCommunicationActions.fetchTableData(),
        DcuToMtuCommunicationActions.fetchCoverageStatistics()
      ])
    ),
  { functional: true }
);

export const tableData$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    dcuToMtuCommunicationService = inject(DcuToMtuCommunicationService)
  ) =>
    actions$.pipe(
      ofType(DcuToMtuCommunicationActions.fetchTableData),
      withLatestFrom(store.select(selectPayload), store.select(selectFiltersRequest)),
      filter(([, , filtersRequest]) => filtersRequest.status === AsyncRequestStatus.Success),
      exhaustMap(([, payload]) =>
        dcuToMtuCommunicationService.getTableData({ ...payload }).pipe(
          map(tableData => DcuToMtuCommunicationActions.fetchTableDataSuccess({ data: tableData })),
          catchError(err => of(DcuToMtuCommunicationActions.fetchTableDataFailure({ error: String(err) })))
        )
      )
    ),
  { functional: true }
);

export const coverageStatistics$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    dcuToMtuCommunicationService = inject(DcuToMtuCommunicationService)
  ) =>
    actions$.pipe(
      ofType(DcuToMtuCommunicationActions.fetchCoverageStatistics),
      withLatestFrom(store.select(selectPayload), store.select(selectFiltersRequest)),
      filter(([, , filtersRequest]) => filtersRequest.status === AsyncRequestStatus.Success),
      exhaustMap(([, payload]) =>
        dcuToMtuCommunicationService.getCoverageStatistics({ ...payload }).pipe(
          map(data => DcuToMtuCommunicationActions.fetchCoverageStatisticsSuccess({ data })),
          catchError(err => of(DcuToMtuCommunicationActions.fetchCoverageStatisticsFailure({ error: String(err) })))
        )
      )
    ),
  { functional: true }
);

export const exportTableData$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    dcuToMtuCommunicationService = inject(DcuToMtuCommunicationService),
    modelService = inject(DcuToMtuCommunicationModelService),
    exportUtils = inject(ExportUtilsService),
    exportFacade = inject(ExportFacadeService)
  ) =>
    actions$.pipe(
      ofType(DcuToMtuCommunicationActions.exportFile),
      withLatestFrom(store.select(selectPayload)),
      switchMap(([action, payload]) =>
        dcuToMtuCommunicationService.getTableData({ ...payload }).pipe(
          take(1),
          switchMap(res => {
            const pageModel = {
              dataSource: {
                getAll: (tableState: DevicesPayload): Observable<TableData<DcuToMtuCommunication>> =>
                  dcuToMtuCommunicationService.getTableData(tableState).pipe(take(1)),
                total: res.total
              },
              exportConfig: modelService.exportConfig
            } as Pick<ListingPageModel, 'dataSource' | 'exportConfig'>;
            const { visibility, order } = action;
            return exportUtils
              .getTableDataForExport(
                { ...payload } as DevicesPayload,
                pageModel as ListingPageModel,
                pageModel.dataSource.total
              )
              .pipe(
                tap(data =>
                  exportFacade.exportRows(
                    action.fileType,
                    data as ExportRow[],
                    visibility,
                    order,
                    pageModel.exportConfig!.fileName!
                  )
                )
              );
          }),
          map(() => DcuToMtuCommunicationActions.exportFileSuccess()),
          catchError(err => {
            console.error('Error fetching data for export:', err);
            return of(DcuToMtuCommunicationActions.exportFileFailure({ error: String(err) }));
          })
        )
      )
    ),
  { functional: true, dispatch: true }
);

export const getFiltersState$ = createEffect(
  (actions$ = inject(Actions), userConfigService = inject(UserConfigService)) =>
    actions$.pipe(
      ofType(DcuToMtuCommunicationActions.getPersistedFiltersState),
      exhaustMap(() =>
        userConfigService.getGridSettings<UserConfigDcuToMtuFilters>(gridBaseKey).pipe(
          map(s =>
            DcuToMtuCommunicationActions.getFiltersAndInitFiltersState({
              filtersState: s?.filters ?? null,
              ...(s?.pageSize != null && { restoredTake: s.pageSize })
            })
          ),
          catchError(() => of(DcuToMtuCommunicationActions.getFiltersAndInitFiltersState({ filtersState: null })))
        )
      )
    ),
  { functional: true }
);

export const getFiltersAndInitFiltersState$ = createEffect(
  (actions$ = inject(Actions), dcuToMtuCommunicationService = inject(DcuToMtuCommunicationService)) =>
    actions$.pipe(
      ofType(DcuToMtuCommunicationActions.getFiltersAndInitFiltersState),
      exhaustMap(({ filtersState, restoredTake }) =>
        dcuToMtuCommunicationService.getReportGroup('Network Monitoring').pipe(
          map(reportGroups =>
            DcuToMtuCommunicationActions.getFiltersAndInitFiltersStateSuccess({
              data: reportGroups,
              filtersState,
              ...(restoredTake !== undefined && { restoredTake })
            })
          ),
          catchError(err => of(DcuToMtuCommunicationActions.fetchReportGroupFailure({ error: String(err) })))
        )
      )
    ),
  { functional: true }
);

export const saveFiltersState$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), userConfig = inject(UserConfigService)) =>
    actions$.pipe(
      ofType(DcuToMtuCommunicationActions.savePersistedFiltersState),
      withLatestFrom(store.select(selectPayload)),
      tap(([, payload]) => {
        userConfig.saveFilters(gridBaseKey, { filters: payload.filters });
      })
    ),
  { functional: true, dispatch: false }
);

export const refresh$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(DcuToMtuCommunicationActions.refresh),
      mergeMap(() => [DcuToMtuCommunicationActions.getPersistedFiltersState()])
    ),
  { functional: true }
);
